import { LayoutItem } from '@blockworks/platform/api/research/studio/dashboard';
import { callWith } from '@blockworks/ui/utils';

import { Visualization } from '../../../visualization';
import { useDashboardGridContext } from '../../context';
import { DashboardMode } from '../../dashboard-grid.model';

import { GridItemSettings } from './grid-item-settings';

type VisualizationItemProps = { item: LayoutItem };

const VisualizationItem = ({ item }: VisualizationItemProps) => {
    const { componentSlots, visualizations, preloadedData, showAllItems, mode } = useDashboardGridContext();

    const componentSlotsProp = {
        HeaderEnd: callWith(GridItemSettings, { itemId: item.i }),
        HeaderTitle: componentSlots?.VisualizationTitle,
    };

    const visualization = visualizations.find(v => v.id === item.data.id);

    if (mode === DashboardMode.Preloaded) {
        const visualizationWithExecution = preloadedData?.find(v => v.id === item.data.id);

        if (item.data.isFeatured || showAllItems) {
            if (!visualizationWithExecution) {
                console.warn(
                    `Visualization ${item.data.id} not found in preloaded data. (${preloadedData?.length} items preloaded)`,
                );
                return <Visualization.Preloaded visualization={visualization} componentSlots={componentSlotsProp} />;
            }

            return (
                <Visualization.PreloadedWithExecution
                    visualization={visualizationWithExecution}
                    componentSlots={componentSlotsProp}
                />
            );
        }

        if (!visualization) {
            console.warn('Visualization not provided.', {
                id: item.data.id,
                visualization,
            });
            return null;
        }
        return <Visualization.Obscured visualization={visualization} componentSlots={componentSlotsProp} />;
    }

    if (!visualization)
        /**
         * If the visualization is not found, it may mean the dashboard is either
         * 1) in edit mode or
         * 2) the visualization was deleted before it was removed from a dashboard
         *
         * In these cases, the dashboard's visualizations array won't include the visualization.
         * Hence, we we use the most robust `Visualization` component which fetches the visualization internally.
         */
        return <Visualization id={item.data.id} componentSlots={componentSlotsProp} />;

    return <Visualization.Preloaded visualization={visualization} componentSlots={componentSlotsProp} />;
};

export { VisualizationItem };
